import React, { useCallback, useState, useEffect } from "react";
import PurchaseDocDetails from "../../_components/Purchasing/PurchaseDocDetails";
import { FieldInput, SearchableSelect, DetailRow } from "../../_components/Purchasing/PurchaseShared";
import { FiHash } from "react-icons/fi";
import {
    getPurchaseInvoiceById,
    updatePurchaseInvoice,
    createPurchaseInvoice,
    getPurchaseOrders,
} from "../../_services/purchase.service";
import { getSuppliers } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";

/* Supplier data fetchers */
let supplierCache = {};
async function getCachedSupplier(supplierName) {
    if (!supplierName) return [];
    if (supplierCache[supplierName]) return supplierCache[supplierName];
    const res = await getSuppliers({ pageSize: 1000, page: 1, search: supplierName, searchType: "name" });
    supplierCache[supplierName] = res?.data || [];
    return supplierCache[supplierName];
}
async function fetchSupplierNames() {
    const res = await getSuppliers({ pageSize: 1000, page: 1 });
    const names = (res?.data || []).filter(r => { const s = (r.status || r.Status || "").toLowerCase(); return s === "active"; }).map((r) => r.name).filter(Boolean);
    return [...new Set(names)].sort();
}
async function fetchContactPersons(supplierName) {
    if (!supplierName) return [];
    const data = await getCachedSupplier(supplierName);
    return data.filter((r) => { const s = (r.status || r.Status || "").toLowerCase(); return r.name === supplierName && !!r.attention && s === "active"; }).map((r) => r.attention ?? "").filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
}
async function fetchSupplierDetails(supplierName, contactPerson = "") {
    if (!supplierName) return {};
    const data = await getCachedSupplier(supplierName);
    const rows = data.filter((r) => r.name === supplierName);
    const first = rows[0];
    if (!first) return {};
    const base = { supplierAddress: first.billingAddress || first.billing_address || first.address || "", supplierShippingAddress: first.shippingAddress || first.shipping_address || "" };
    if (!contactPerson) return base;
    const match = rows.find((r) => (r.attention || "") === contactPerson) || first;
    return { ...base, paymentTerms: match.creditTerms || match.credit_terms || "", phoneNumber: match.phoneNumber || match.phone_number || "" };
}

const fetchDirectPI = (id) => getPurchaseInvoiceById(id).then(res => {
    if (res.piType !== "Direct") throw new Error("Not a Direct PI");
    return res;
});

export default function DirectPurchaseInvoiceDetails() {
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);
    const [poList, setPoList] = useState([]);

    useEffect(() => {
        getPurchaseOrders({ pageSize: 1500 }).then(res => setPoList(res?.data || [])).catch(() => { });
    }, []);

    const handlePoChange = (e, baseOnChange) => {
        const val = e.target.value;
        baseOnChange({ target: { name: "poRefNo", value: val } });
        const po = poList.find(p => p.id === val);
        if (po && po.items?.length > 0) {
            baseOnChange({ target: { name: "items", value: po.items } });
        }
    };

    const extraFields = {
        left: ({ draft, onChange, errors, edit }) => {
            if (edit) {
                const options = poList
                    .filter(po => po.status === "Approved" && po.supplier === draft.supplier)
                    .map(po => po.id)
                    .filter(Boolean);

                return (
                    <>
                        <SearchableSelect
                            label="PO Reference No."
                            name="poRefNo"
                            value={draft.poRefNo || ""}
                            options={options}
                            onChange={(e) => handlePoChange(e, onChange)}
                            placeholder="Select PO reference..."
                        />
                        <FieldInput
                            label="Inv Reference No"
                            name="refNo"
                            value={draft.refNo || ""}
                            onChange={onChange}
                            required
                            error={errors?.refNo}
                            placeholder="Enter invoice reference no..."
                        />
                    </>
                );
            }
            return (
                <>
                    {draft.poRefNo && (
                        <DetailRow icon={FiHash} label="PO Reference No.">{draft.poRefNo}</DetailRow>
                    )}
                    {draft.refNo && (
                        <DetailRow icon={FiHash} label="Inv Reference No">{draft.refNo}</DetailRow>
                    )}
                </>
            );
        }
    };

    const wrapCreate = async (data) => {
        return createPurchaseInvoice({ ...data, piType: "Direct" });
    };

    const wrapUpdate = async (id, data) => {
        return updatePurchaseInvoice(id, { ...data, piType: "Direct" });
    };

    return (
        <PurchaseDocDetails
            docType="Direct Purchase Invoice"
            basePath="/finance/direct-purchase-invoices"
            fetchById={fetchDirectPI}
            updateItem={wrapUpdate}
            createItem={wrapCreate}
            fetchSupplierNames={fetchSupplierNames}
            fetchContactPersons={fetchContactPersons}
            fetchSupplierDetails={fetchSupplierDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            searchable={false}
            searchSource="RM"
            extraFields={extraFields}
        />
    );
}